/**
 * Managing the standard scene lighting setup involving a sun and ambient lighting for outdoors and interior.
 * @module
 */
import { getOS, OS } from "common/utils/context";
import degreeToRadian from "common/utils/degreeToRadian";
import { productLog } from "common/utils/logging";
import { vec2RotateAround } from "geom/vec2";
import * as THREE from "three";
import { Vector3 } from "three";
import { colorUnsignedByteArrayToCSSHexString, lightIntensityToThreePhysicalLight } from "./convert";
import { UI_LAYER } from "./layerConstants";
import { allLayers, exteriorLightLayers, interiorLightLayers } from "./layers";
import type { DynamicSkyHemiLightParams, DynamicSkyParams } from "./materials/dynamicSkyMaterial";
import getDynamicSkyMaterial, { assignSkyMaterialUniforms } from "./materials/dynamicSkyMaterial";
import type { FogParams, InteriorLightSettings, SceneLightSettings } from "./sceneLights";
import THREEAssetLoader from "./THREEAssetLoader";
import type { SSFXRendererSceneForLights } from "./threejs/higharc/SSFXRendererTypes";

let skyGeo;

let loadedEnvMapResolution: number | undefined;

const SCENE_LIGHTS_GUI_FOLDER_NAME = "Scene Lights";
const SKYBOX_GUI_FOLDER_NAME = "Skybox";

function logColor(color: number[] | THREE.Color | string) {
  if (color instanceof THREE.Color) {
    return `new Color(0x${color.getHexString(THREE.SRGBColorSpace)}})`;
  } else if (typeof color === "string") {
    if (color.startsWith("#")) {
      return `new Color(0x${color.substring(1)})`;
    } else {
      return `new Color("${color}")`;
    }
  } else {
    return `new Color("${colorUnsignedByteArrayToCSSHexString(color)}")`;
  }
}

function adjustSunlight(pos: THREE.Vector3, target: THREE.Vector3, angle: number): THREE.Vector3 {
  const newPos: THREE.Vector3 = pos.clone();
  const newXY = vec2RotateAround(pos.x, pos.y, angle, target.x, target.y);
  newPos.x = newXY[0];
  newPos.y = newXY[1];
  return newPos;
}

export interface SceneLightingModeSettings {
  exteriorLightProbesEnabled: boolean;
  interiorLightProbesEnabled: boolean;
  interiorAmbientLightEnabled: boolean;
  siteRotation: number;
  altitudeAzimuthOverride?: [number, number];
  radialFogCenterX: number;
  radialFogCenterY: number;
}

const defaultModeSettings: SceneLightingModeSettings = Object.freeze({
  exteriorLightProbesEnabled: true,
  interiorLightProbesEnabled: true,
  interiorAmbientLightEnabled: true,
  siteRotation: 0,
  radialFogCenterX: 0,
  radialFogCenterY: 0,
});

export function toCartesian(altitudeAzimuthOverride: [number, number]): Vector3 {
  const x = Math.cos(altitudeAzimuthOverride[1]);
  const y = Math.sin(altitudeAzimuthOverride[1]);
  const z = Math.sin(altitudeAzimuthOverride[0]);

  return new Vector3(x, y, z);
}

function getShadowResolution() {
  const os = getOS();
  if (os === OS.IPHONE || os === OS.ANDROID) {
    return 2048;
  }
  if (os === OS.IPAD) {
    return 4096;
  }
  return 6144;
}

export class SceneLightsManager {
  toneMappingEnabled: boolean = true;
  toneMappingExposure: number = 1.0;

  readonly hemisphereLight: THREE.HemisphereLight;
  readonly directionalLight: THREE.DirectionalLight;
  readonly ambientLight: THREE.AmbientLight;
  readonly interiorAmbientLight: THREE.AmbientLight;
  readonly skybox: THREE.Mesh;
  readonly skyMaterial: THREE.ShaderMaterial;

  // When rendering lightprobes, the sky is mixed together with a fake sky that behaves like a hemi light.
  // This parameter controls the mixing.
  private lightProbeSkyHemiLightFactor: number = 0.6;

  private skyBaseParams: Pick<DynamicSkyParams, "dirLight" | "upVector">;
  private skyParams: Partial<DynamicSkyParams> = {};
  private hemiLightSkyParams: Omit<DynamicSkyHemiLightParams, "hemiLightFactor"> | undefined;

  private interiorLightSettings: InteriorLightSettings;

  private fog: FogParams = {
    color: new THREE.Color(0xc1c1c1),
    near: 40000,
    far: 50000,
  };

  radialFogCenterX: number = 0;
  radialFogCenterY: number = 0;
  radialFogNear: number = 1000000;
  radialFogFar: number = 10000000;

  probeIrradianceSaturationMultiplier: number = 0.5;

  private guiParent: any;

  readonly group: THREE.Group;

  constructor(private readonly scene: SSFXRendererSceneForLights) {
    this.group = new THREE.Group();
    this.hemisphereLight = new THREE.HemisphereLight();
    this.hemisphereLight.layers = exteriorLightLayers();
    this.group.add(this.hemisphereLight);
    this.hemisphereLight.visible = false; // The hemisphere light will only be visible if light probes are disabled.

    this.directionalLight = new THREE.DirectionalLight();
    this.directionalLight.layers = allLayers();
    this.group.add(this.directionalLight);

    this.ambientLight = new THREE.AmbientLight();
    this.ambientLight.layers = exteriorLightLayers();
    this.group.add(this.ambientLight);

    this.interiorAmbientLight = new THREE.AmbientLight();
    this.interiorAmbientLight.layers = interiorLightLayers();
    this.group.add(this.interiorAmbientLight);

    this.interiorLightSettings = {
      intensityMultiplier: 1.0,
      decayMultiplier: 1.0,
    };

    if (skyGeo === undefined) {
      skyGeo = new THREE.SphereGeometry(2000, 32, 15);
      skyGeo.userData.isCached = true;
    }

    this.skyBaseParams = {
      dirLight: this.directionalLight,
      upVector: new THREE.Vector3(0, 0, 1),
    };

    this.skyMaterial = getDynamicSkyMaterial(this.skyBaseParams);
    this.skybox = new THREE.Mesh(skyGeo, this.skyMaterial);
    this.skybox.renderOrder = -1;
    this.skybox.name = "Skybox";
    this.skybox.userData.excludeFromExport = true;
    this.group.add(this.skybox);
  }

  setupDirectionalLightShadow(showShadowCamera: boolean) {
    const shadowResolution = getShadowResolution();

    this.directionalLight.castShadow = true;

    this.directionalLight.shadow.bias = -0.0005;
    this.directionalLight.shadow.normalBias = 0.5;

    this.directionalLight.shadow.mapSize.x = shadowResolution;
    this.directionalLight.shadow.mapSize.y = shadowResolution;

    this.directionalLight.target.position.set(0, 0, 0);
    this.group.add(this.directionalLight.target);

    this.directionalLight.shadow.camera.layers.disable(UI_LAYER);

    this.directionalLight.shadow.camera.up.set(0, 0, 1);

    // In lowQualityMode, our goal here is to have a big enough area to include the entire building.
    // In normal mode, we try to include the site objects around it as well.
    const lowQualityMode = shadowResolution < 4096;
    this.directionalLight.shadow.camera.left = lowQualityMode ? -1400 : -2000;
    this.directionalLight.shadow.camera.right = lowQualityMode ? 1400 : 2000;
    this.directionalLight.shadow.camera.top = lowQualityMode ? 1400 : 2000;
    this.directionalLight.shadow.camera.bottom = lowQualityMode ? -1400 : -2000;

    this.directionalLight.shadow.camera.near = 50;
    this.directionalLight.shadow.camera.far = 5000;

    if (showShadowCamera) {
      this.group.add(new THREE.CameraHelper(this.directionalLight.shadow.camera));
    }
  }

  async renderSceneLights(settings: SceneLightSettings, userModeSettings: Partial<SceneLightingModeSettings>) {
    const modeSettings = { ...defaultModeSettings, ...userModeSettings };
    this.hemisphereLight.visible = !modeSettings.exteriorLightProbesEnabled;
    if (this.hemisphereLight.visible) {
      if (settings.hemisphereLight.position) {
        this.hemisphereLight.position.copy(settings.hemisphereLight.position);
      }
      if (settings.hemisphereLight.intensity) {
        this.hemisphereLight.intensity = lightIntensityToThreePhysicalLight(settings.hemisphereLight.intensity);
      }
      if (settings.hemisphereLight.color) {
        this.hemisphereLight.color.set(settings.hemisphereLight.color);
      }
      if (settings.hemisphereLight.groundColor) {
        this.hemisphereLight.groundColor.set(settings.hemisphereLight.groundColor);
      }
      this.hemisphereLight.updateMatrixWorld();
    }
    if (settings.ambientLight.intensity) {
      this.ambientLight.intensity = lightIntensityToThreePhysicalLight(settings.ambientLight.intensity);
    }
    if (settings.ambientLight.color) {
      this.ambientLight.color.set(settings.ambientLight.color);
    }

    const dirLight = settings.directionalLight;
    let newPos = adjustSunlight(
      dirLight.position ?? new Vector3(),
      this.directionalLight.target.position,
      degreeToRadian(modeSettings.siteRotation),
    );
    if (userModeSettings?.altitudeAzimuthOverride) {
      const v = toCartesian(userModeSettings.altitudeAzimuthOverride);
      newPos = v;
    }
    this.directionalLight.position.copy(newPos);
    if (dirLight.intensity) {
      this.directionalLight.intensity = lightIntensityToThreePhysicalLight(dirLight.intensity);
    }
    if (dirLight.color) {
      this.directionalLight.color.set(dirLight.color);
    }
    this.directionalLight.updateMatrixWorld();

    this.interiorAmbientLight.visible = modeSettings.interiorAmbientLightEnabled;
    if (this.interiorAmbientLight.visible) {
      const interiorLightSettings = modeSettings.interiorLightProbesEnabled
        ? settings.interiorAmbientLight
        : settings.interiorNoProbesAmbientLight;
      if (interiorLightSettings?.intensity) {
        this.interiorAmbientLight.intensity = lightIntensityToThreePhysicalLight(interiorLightSettings.intensity);
      }
      if (interiorLightSettings?.color) {
        this.interiorAmbientLight.color.set(interiorLightSettings.color);
      }
    }

    if (settings.sky) {
      this.skyParams = settings.sky;
    }
    // These uniforms are used when rendering a faked sky to lightprobes, so we can control secondary lighting.
    this.hemiLightSkyParams = {
      hemiLightHorizontal: settings.lightProbeSkyHemiLightHorizontal,
      hemiLightTopColor: settings.hemisphereLight.color ?? new THREE.Color(),
      hemiLightHorizonColor: settings.hemisphereLight.groundColor ?? new THREE.Color(),
      hemiLightBottomColor: settings.hemisphereLight.groundColor ?? new THREE.Color(),
      hemiLightIntensity: settings.hemisphereLight.intensity ?? 0,
    };
    assignSkyMaterialUniforms(this.skyMaterial, {
      ...this.skyBaseParams,
      ...this.skyParams,
      ...this.hemiLightSkyParams,
    });

    this.interiorLightSettings = { ...this.interiorLightSettings, ...settings.interiorLightSettings };

    this.lightProbeSkyHemiLightFactor = settings.lightProbeSkyHemiLightFactor;

    this.toneMappingEnabled = settings.toneMappingEnabled;
    this.toneMappingExposure = settings.toneMappingExposure;

    this.fog = settings.fog;
    this.radialFogNear = settings.fog.radialFogNear ?? 1000000;
    this.radialFogFar = settings.fog.radialFogFar ?? 10000000;
    this.radialFogCenterX = modeSettings.radialFogCenterX;
    this.radialFogCenterY = modeSettings.radialFogCenterY;

    this.probeIrradianceSaturationMultiplier = settings.probeIrradianceSaturationMultiplier;

    await this.setEnvMap(settings.exteriorEnvMap, settings.exteriorEnvMapEXR);

    this.reinitGUI();
  }

  async setEnvMap(envMapURL: string | string[], isEXR: boolean): Promise<void> {
    if (!envMapURL) {
      this.scene.environment = null;
    } else {
      const assetLoader = THREEAssetLoader.instance;
      if (typeof envMapURL === "string") {
        const envMap: THREE.Texture = await assetLoader.loadTexture(envMapURL, isEXR);
        this.scene.environment = envMap;
      } else {
        const envMap: THREE.CubeTexture = await assetLoader.loadCubeTexture(envMapURL);
        envMap.format = THREE.RGBAFormat;
        envMap.mapping = THREE.CubeReflectionMapping;
        envMap.colorSpace = THREE.SRGBColorSpace;
        const envMapImages = envMap.images as Array<{ width: number }>;
        if (loadedEnvMapResolution === undefined) {
          loadedEnvMapResolution = envMapImages[0].width;
        } else if (envMapImages[0].width !== loadedEnvMapResolution) {
          console.warn(
            `Envmap from ${envMapURL[0]} has different resolution (${envMapImages[0].width}) than previously loaded envMap (${loadedEnvMapResolution}). This can cause extra shaders to be loaded.`,
          );
        }
        this.scene.environment = envMap;
      }
    }
  }

  // Can be called repeatedly. GUI should be passed in when called for the first time.
  initGUI(guiParent?: dat.GUI) {
    if (guiParent !== undefined) {
      this.guiParent = guiParent;
    }
    this.reinitGUI();
  }

  private reinitGUI() {
    if (this.guiParent) {
      this.initSkyboxGUI({ ...this.skyParams, ...this.hemiLightSkyParams });
      this.initFogGUI(this.fog, "Fog", "fog");
      this.initProbeIrradianceSaturationGUI();
    }
  }

  private initFogGUI(fogParams: FogParams, folderName: string, logName: string) {
    if (this.guiParent.__folders[folderName]) {
      this.guiParent.removeFolder(this.guiParent.__folders[folderName]);
    }

    const fogGUI = this.guiParent.addFolder(folderName);

    const params = {};

    const assign = () => {
      for (const paramId in fogParams) {
        if (fogParams[paramId] instanceof THREE.Color) {
          fogParams[paramId].setStyle(params[paramId]);
        } else if (typeof fogParams[paramId] === "number") {
          fogParams[paramId] = params[paramId];
        }
      }
      this.scene.dirtyAllLightProbes();
      this.scene.container?.markRenderingNeedsUpdate();
    };

    for (const paramId in fogParams) {
      const param = fogParams[paramId];
      if (param instanceof THREE.Color) {
        params[paramId] = "#" + param.getHexString(THREE.SRGBColorSpace);
        fogGUI.addColor(params, paramId).onChange(assign);
      } else if (typeof param === "number") {
        params[paramId] = param;
        fogGUI.add(params, paramId).onChange(assign);
      }
    }

    params["log code"] = () => {
      const fogParamsLog = `${logName}: {
  ${Object.keys(fogParams)
    .map((paramId) => {
      if (fogParams[paramId] instanceof THREE.Color) {
        return `${paramId}: ${logColor(params[paramId])}`;
      } else if (typeof fogParams[paramId] === "number") {
        return `${paramId}: ${params[paramId]}`;
      }
      return undefined;
    })
    .join(",\n  ")}
}`;
      productLog(fogParamsLog);
    };
    fogGUI.add(params, "log code");
  }

  private initProbeIrradianceSaturationGUI() {
    if (this.guiParent.__folders["Probe Irradiance Saturation"]) {
      this.guiParent.removeFolder(this.guiParent.__folders["Probe Irradiance Saturation"]);
    }
    this.guiParent
      .addFolder("Probe Irradiance Saturation")
      .add(this, "probeIrradianceSaturationMultiplier", 0.0, 1.0)
      .step(0.01)
      .onChange(() => {
        this.scene.dirtyAllLightProbes();
        this.scene.container?.markRenderingNeedsUpdate();
      });
  }

  private initSkyboxGUI(skyParams: Partial<DynamicSkyParams>) {
    if (this.guiParent.__folders[SKYBOX_GUI_FOLDER_NAME]) {
      this.guiParent.removeFolder(this.guiParent.__folders[SCENE_LIGHTS_GUI_FOLDER_NAME]);
      this.guiParent.removeFolder(this.guiParent.__folders[SKYBOX_GUI_FOLDER_NAME]);
    }

    const sceneLightsGUI = this.guiParent.addFolder(SCENE_LIGHTS_GUI_FOLDER_NAME);
    const skyboxGUI = this.guiParent.addFolder(SKYBOX_GUI_FOLDER_NAME);

    const markRenderingNeedsUpdate = () => {
      if (this.scene.container) {
        this.scene.container.markRenderingNeedsUpdate();
      }
    };
    const refreshLighting = (dirtyProbesOnChange: boolean = true) => {
      if (dirtyProbesOnChange) {
        this.scene.dirtyAllLightProbes();
      }
      markRenderingNeedsUpdate();
    };

    sceneLightsGUI.add(this, "toneMappingEnabled").onChange(markRenderingNeedsUpdate);
    sceneLightsGUI.add(this, "toneMappingExposure", 0.1, 5.0).onChange(markRenderingNeedsUpdate);

    function addLightControls(
      light: THREE.Light,
      lightName: string,
      maxIntensity: number,
      dirtyProbesOnChange: boolean = true,
    ) {
      const lightParams: any = {};
      const intensityKey = lightName + "Intensity";
      lightParams[intensityKey] = light.intensity;
      sceneLightsGUI.add(lightParams, intensityKey, 0.0, maxIntensity).onChange(() => {
        light.intensity = lightParams[intensityKey];
        refreshLighting(dirtyProbesOnChange);
      });
      const colorKey = lightName + "Color";
      lightParams[colorKey] = "#" + light.color.getHexString(THREE.SRGBColorSpace);
      sceneLightsGUI.addColor(lightParams, colorKey).onChange(() => {
        light.color.setStyle(lightParams[colorKey]);
        refreshLighting(dirtyProbesOnChange);
      });
    }

    addLightControls(this.directionalLight, "directionalLight", 5.0);
    addLightControls(this.ambientLight, "ambientLight", 1.0);
    addLightControls(this.interiorAmbientLight, "interiorAmbientLight", 1.0, false);

    const refreshInteriorLights = () => {
      for (const lightMap of this.scene.layerSpecificUniforms.lightOffsetMaps) {
        (lightMap.uniforms.lightOffsetMapColor as THREE.Color).multiplyScalar(
          this.interiorLightSettings.intensityMultiplier / lightMap.appliedIntensityMultiplier,
        );
        lightMap.appliedIntensityMultiplier = this.interiorLightSettings.intensityMultiplier;
        (lightMap.uniforms.lightOffsetMapDecay as number) *=
          this.interiorLightSettings.decayMultiplier / lightMap.appliedDecayMultiplier;
        lightMap.appliedDecayMultiplier = this.interiorLightSettings.decayMultiplier;
      }
      refreshLighting(false);
    };

    const interiorLightSettingsGUI = sceneLightsGUI.addFolder("Interior lights");
    interiorLightSettingsGUI
      .add(this.interiorLightSettings, "intensityMultiplier", 0.5, 4.0)
      .onChange(refreshInteriorLights);
    interiorLightSettingsGUI
      .add(this.interiorLightSettings, "decayMultiplier", 0.5, 2.0)
      .onChange(refreshInteriorLights);

    sceneLightsGUI.add(this, "lightProbeSkyHemiLightFactor", 0.0, 1.0).onChange(refreshLighting);

    const sceneLightsLogger = {
      "log code": () => {
        const lightLog = `
  toneMappingEnabled: ${this.toneMappingEnabled},
  toneMappingExposure: ${this.toneMappingExposure},
  interiorNoProbesAmbientLight: {
    intensity: ${this.interiorAmbientLight.intensity},
    color: ${logColor(this.interiorAmbientLight.color)},
  },
  interiorLightSettings {
    intensityMultiplier: ${this.interiorLightSettings.intensityMultiplier},
    decayMultiplier: ${this.interiorLightSettings.decayMultiplier},
  },
  directionalLight: {
    position: new Vector3(${this.directionalLight.position.x}, ${this.directionalLight.position.y}, ${
      this.directionalLight.position.z
    }),
    intensity: ${this.directionalLight.intensity},
    color: ${logColor(this.directionalLight.color)},
  },
  ambientLight: {
    intensity: ${this.ambientLight.intensity},
    color: ${logColor(this.ambientLight.color)},
  }`;
        productLog(lightLog);
      },
    };
    sceneLightsGUI.add(sceneLightsLogger, "log code");

    const params: any = {};

    const assign = () => {
      const newParamValues = {};

      for (const paramId in skyParams) {
        if (skyParams[paramId] instanceof THREE.Color) {
          newParamValues[paramId] = new THREE.Color(params[paramId]);
        } else if (typeof skyParams[paramId] === "number") {
          newParamValues[paramId] = params[paramId];
        }
      }

      assignSkyMaterialUniforms(this.skyMaterial, { ...this.skyBaseParams, ...newParamValues });
      this.scene.dirtyAllLightProbes();
    };

    for (const paramId in skyParams) {
      const param = skyParams[paramId];
      if (param instanceof THREE.Color) {
        params[paramId] = "#" + param.getHexString(THREE.SRGBColorSpace);
        skyboxGUI.addColor(params, paramId).onChange(assign);
      } else if (typeof param === "number") {
        params[paramId] = param;
        if (paramId === "hemiLightHorizontal" || paramId.endsWith("BlendAlpha")) {
          skyboxGUI.add(params, paramId, 0, 1).onChange(assign);
        } else {
          skyboxGUI.add(params, paramId).onChange(assign);
        }
      }
    }

    params["log code"] = () => {
      const skyParamsLog = `sky: {
  ${Object.keys(skyParams)
    .filter((paramId) => !paramId.startsWith("hemiLight"))
    .map((paramId) => {
      if (skyParams[paramId] instanceof THREE.Color) {
        return `${paramId}: ${logColor(params[paramId])}`;
      } else if (typeof skyParams[paramId] === "number") {
        return `${paramId}: ${params[paramId]}`;
      }
      return undefined;
    })
    .join(",\n  ")}
}`;
      productLog(skyParamsLog);
      const hemiLightLog = `hemisphereLight: {
  position: new Vector3(0, 0, 1),
  intensity: ${params.hemiLightIntensity},
  color: ${logColor(params.hemiLightTopColor)},
  groundColor: ${logColor(params.hemiLightHorizonColor)},
},
lightProbeSkyHemiLightHorizontal: ${params.hemiLightHorizontal},`;
      productLog(hemiLightLog);
    };
    skyboxGUI.add(params, "log code");
  }

  onBeforeRender(scene: THREE.Scene, camera: THREE.Camera, isRenderingLightProbe: boolean) {
    if (scene.fog) {
      const fog = scene.fog as THREE.Fog;
      fog.color.copy(this.fog.color);
      fog.near = this.fog.near;
      fog.far = this.fog.far;
    }

    this.skyMaterial.uniforms.hemiLightFactor.value = isRenderingLightProbe ? this.lightProbeSkyHemiLightFactor : 0.0;

    // The skybox is set up so that it's rendered first without a depth test.
    camera.getWorldPosition(this.skybox.position);
    this.skybox.updateMatrixWorld();
  }
}
